import { useEffect, useRef } from 'react'
import { api } from '@/lib/api-client.ts'

/**
 * Throttled, online-only presence ping. Updates User.lastSeenAt so the backend
 * can gate "push only when away". Fires on mount, window focus,
 * visibilitychange→visible, reconnect, and on a ~60s interval while the tab is
 * visible. Fire-and-forget (never queued) — a missed heartbeat just means the
 * user looks away a little longer, which is harmless.
 */
const HEARTBEAT_INTERVAL_MS = 60_000
const MIN_GAP_MS = 50_000

export function usePresenceHeartbeat(): void {
  const lastSentRef = useRef<number>(0)

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined

    const beat = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return

      const now = Date.now()
      if (now - lastSentRef.current < MIN_GAP_MS) return
      lastSentRef.current = now

      // Online-only, best-effort: swallow errors (offline, 401 on logout race).
      void api.post('/presence/heartbeat').catch(() => {})
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') beat()
    }

    beat()
    intervalId = setInterval(beat, HEARTBEAT_INTERVAL_MS)
    window.addEventListener('focus', beat)
    window.addEventListener('online', beat)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      if (intervalId) clearInterval(intervalId)
      window.removeEventListener('focus', beat)
      window.removeEventListener('online', beat)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])
}
